/**
 * Responsibility type helpers for service actions.
 *
 * The API returns `default_responsibility_type` as an integer
 * (1 = required, 2 = optional, 3 = client_responsibility), while some
 * payloads/legacy records use the string keys. These helpers normalize
 * both shapes so callers never call string methods on a number.
 */

export const RESPONSIBILITY_TYPES = {
  1: 'required',
  2: 'optional',
  3: 'client_responsibility',
}

export const RESPONSIBILITY_VARIANTS = {
  required: 'danger',
  optional: 'warning',
  client_responsibility: 'info',
}

const DEFAULT_VARIANT = 'secondary'

const VALID_TYPES = Object.values(RESPONSIBILITY_TYPES)

/**
 * Normalize a raw responsibility type into one of the string keys.
 * @param {number|string|null|undefined} value
 * @returns {string|null}
 */
export function resolveResponsibilityType(value) {
  if (typeof value === 'number') return RESPONSIBILITY_TYPES[value] ?? null
  if (typeof value === 'string' && VALID_TYPES.includes(value)) return value
  return null
}

/**
 * Human readable label, e.g. `client_responsibility` -> `Client Responsibility`.
 * @param {number|string|null|undefined} value
 * @param {string} [fallback]
 * @returns {string}
 */
export function formatResponsibilityType(value, fallback = 'N/A') {
  const type = resolveResponsibilityType(value)
  if (!type) return fallback
  return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
}

/**
 * Bootstrap badge variant for a responsibility type.
 * @param {number|string|null|undefined} value
 * @returns {string}
 */
export function getResponsibilityVariant(value) {
  const type = resolveResponsibilityType(value)
  if (!type) return DEFAULT_VARIANT
  return RESPONSIBILITY_VARIANTS[type] || DEFAULT_VARIANT
}
